import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {SalesDocumentStatusBadge} from 'shells/lead-sidebar/sales-documents/sales-document-status-badge';
import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';
import {Link} from 'shells/link/link';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import type {SalesDocumentSortOrder} from 'nutshell-graphql-types';
import type {
    GetQuoteById_quote as QuoteType,
    GetQuoteTemplate_quoteTemplate as QuoteTemplateType,
    GetInvoiceById_invoice as InvoiceType,
    GetInvoiceTemplate_invoiceTemplate as InvoiceTemplateType,
} from 'nutshell-graphql-types';

import {getQuoteTimes} from '../../../lead/sales-documents/shared';
import type {ChartReportParams} from '../../../reports/report-types';

/**
 * Needed to support the passing of QuoteTemplate data through the shared flow
 * of components that expect a quote. The fake data here will never be used by
 * the components as the template page is not expected to use any non-templated
 * fields.
 */
export function convertTemplateToQuote(template: QuoteTemplateType): QuoteType {
    return {
        // @ts-expect-error
        __typename: 'Quote',
        ...template,
        displayStatus: 'DRAFT',
        statusLog: '',
        displayNumber: '0-1',
        subtotal: {
            __typename: 'Money',
            amount: 0,
            currency: 'USD',
            formatted: '$0',
            formattedWithCents: '$0.00',
            symbol: '$',
        },
        total: {
            __typename: 'Money',
            amount: 0,
            currency: 'USD',
            formatted: '$0',
            formattedWithCents: '$0.00',
            symbol: '$',
        },
        slug: '',
        productLineItems: {__typename: 'QuoteProductLineItemsConnection', totalCount: 0, edges: []},
        subtotalLineItems: {
            __typename: 'QuoteSubtotalLineItemsConnection',
            totalCount: 0,
            edges: [],
        },
        preparedTime: '',
        preparedTimeFormatted: '',
        acceptedTime: null,
        archivedTime: null,
        revokedTime: null,
        sentTime: null,
        publishedTime: null,
        validUntilDate: '',
        validUntilDateFormatted: '',
        preparedBy: '',
        preparedFor: '',
        shareableViewInBrowserLink: '',
        lead: {
            __typename: 'Lead',
            id: '',
        },
    };
}

/**
 * Needed to support the passing of InvoiceTemplate data through the shared flow
 * of components that expect an invoice. The fake data here will never be used by
 * the components as the template page is not expected to use any non-templated
 * fields.
 */
export function convertTemplateToInvoice(template: InvoiceTemplateType): InvoiceType {
    return {
        // @ts-expect-error
        __typename: 'Invoice',
        ...template,
        displayStatus: 'DRAFT',
        statusLog: '',
        displayNumber: '0-1',
        subtotal: {
            __typename: 'Money',
            amount: 0,
            currency: 'USD',
            formatted: '$0',
            formattedWithCents: '$0.00',
            symbol: '$',
        },
        total: {
            __typename: 'Money',
            amount: 0,
            currency: 'USD',
            formatted: '$0',
            formattedWithCents: '$0.00',
            symbol: '$',
        },
        slug: '',
        productLineItems: {__typename: 'QuoteProductLineItemsConnection', totalCount: 0, edges: []},
        subtotalLineItems: {
            __typename: 'QuoteSubtotalLineItemsConnection',
            totalCount: 0,
            edges: [],
        },
        preparedTime: '',
        preparedTimeFormatted: '',
        acceptedTime: null,
        archivedTime: null,
        revokedTime: null,
        sentTime: null,
        publishedTime: null,
        validUntilDate: '',
        validUntilDateFormatted: '',
        preparedBy: '',
        preparedFor: '',
        shareableViewInBrowserLink: '',
        lead: {
            __typename: 'Lead',
            id: '',
        },
        completionComment: '',
        overdueDuration: null,
    };
}

type Option = {
    value: string;
    label: string;
};

export const QUOTE_STATUS_OPTIONS: Array<Option> = [
    {
        value: 'DRAFT',
        label: 'Draft',
    },
    {
        value: 'PUBLISHED',
        label: 'Ready',
    },
    {
        value: 'SENT',
        label: 'Sent',
    },
    {
        value: 'ACCEPTED',
        label: 'Accepted',
    },
    {
        value: 'REVOKED',
        label: 'Revoked',
    },
    {
        value: 'ARCHIVED',
        label: 'Removed',
    },
];

export const INVOICE_STATUS_OPTIONS: Array<Option> = [
    {
        value: 'DRAFT',
        label: 'Draft',
    },
    {
        value: 'PUBLISHED',
        label: 'Ready',
    },
    {
        value: 'SENT',
        label: 'Sent',
    },
    {
        value: 'ACCEPTED',
        label: 'Paid',
    },
    {
        value: 'REVOKED',
        label: 'Voided',
    },
    {
        value: 'ARCHIVED',
        label: 'Removed',
    },
];

export const getTableColumns = (variant: SalesDocumentType = 'Quote'): Array<any> => {
    const variantLower = variant.toLowerCase();

    return [
        {
            id: `${variantLower}Number`,
            Header: `${variant} number`,
            dataType: 'string',
            // @ts-expect-error
            accessor: (row) => row.displayNumber,
            Cell: ({row}: any) => {
                return (
                    <div className='flex align-center gap-8'>
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            to={{
                                hash: row.original.modalUrl,
                                state: {modal: true},
                                search: window.location.search,
                            }}
                            variant='primary'
                            size='small'
                            title={`Open ${variantLower}`}
                            replace={true}
                        >
                            {row.original.displayNumber}
                        </Link>
                    </div>
                );
            },
            disableSortBy: true,
        },
        {
            id: 'lead',
            Header: 'Lead',
            // @ts-expect-error
            accessor: (row) => {
                return {
                    name: row.lead.name,
                    to: row.lead.htmlUrl,
                };
            },
            dataType: 'linkedName',
            Cell: ({row}: any) => {
                const lead = row.original.lead;

                return (
                    <div className='flex align-center gap-8 word-break'>
                        <Link
                            href={lead.htmlUrl}
                            // @ts-expect-error
                            target='_blank'
                            variant='primary'
                            size='small'
                            title='Open lead page'
                        >
                            {lead.name}
                        </Link>
                    </div>
                );
            },
            // @ts-expect-error
            sortType: (rowA, rowB, columnId) => {
                const leadA = rowA.values[columnId].name;
                const leadB = rowB.values[columnId].name;

                return leadA.localeCompare(leadB);
            },
            disableSortBy: true,
        },
        {
            id: 'owner',
            Header: 'Owner',
            // @ts-expect-error
            accessor: (row) => (row.lead.assignee && row.lead.assignee.name) || 'Unassigned',
            dataType: 'string',
            disableSortBy: true,
        },
        {
            id: 'status',
            Header: 'Status',
            // @ts-expect-error
            accessor: (row) => {
                return {
                    status: row.displayStatus,
                    type: variant,
                };
            },
            dataType: 'statusBadge',
            Cell: ({row}: any) => {
                return (
                    <SalesDocumentStatusBadge
                        status={row.original.displayStatus}
                        type={variant}
                        invoiceOverdueDuration={row.original.overdueDuration}
                        salesDocumentTimes={getQuoteTimes(row.original)}
                    />
                );
            },
            disableSortBy: true,
        },
        {
            id: 'hasBeenViewed',
            Header: 'Viewed',
            // @ts-expect-error
            accessor: (row) => {
                return {
                    hasBeenViewed: row.hasBeenViewed,
                };
            },
            maxWidth: 84, // Width to fully show 'Viewed' and a filter icon
            dataType: 'iconStatus',
            centerAlign: true,
            Cell: ({row}: any) => {
                if (row.original.hasBeenViewed) {
                    return <EyeIcon size={20} />;
                }

                return null;
            },
            disableSortBy: true,
        },
        {
            id: 'modifiedTime',
            Header: 'Last update',
            // @ts-expect-error
            accessor: (row) => {
                return {
                    display: row.modifiedTime,
                    timestamp: row.modifiedTime,
                };
            },
            Cell: ({row}: any) => {
                return (
                    <FormattedTimestamp
                        datetime={row.original.modifiedTime}
                        format={FormattedTimestamp.formats.shortDayMonthYearWithAtTime}
                    />
                );
            },
            // @ts-expect-error
            sortType: (rowA, rowB, columnId) => {
                const timestampA = rowA.values[columnId].timestamp;
                const timestampB = rowB.values[columnId].timestamp;

                return timestampA - timestampB;
            },
            dataType: 'timestamp',
        },
        {
            id: 'value',
            Header: 'Value',
            // @ts-expect-error
            accessor: (row) => {
                return {
                    display: row.total.formattedWithCents,
                    value: row.total.amount,
                };
            },
            Cell: ({row}: any) => {
                return <Body size='small'>{row.original.total.formattedWithCents}</Body>;
            },
            dataType: 'string',
            maxWidth: 110,
            // @ts-expect-error
            sortType: (rowA, rowB, columnId) => {
                const valueA = rowA.values[columnId].value;
                const valueB = rowB.values[columnId].value;

                return valueA - valueB;
            },
            disableSortBy: !NutClientConfig.hasSalesDocumentGraphsFF,
        },
    ];
};

export function getSortOrderEnum(sortBy: string): SalesDocumentSortOrder {
    switch (sortBy) {
        case 'modifiedTime':
            return 'MODIFIED_TIME_ASC';
        case 'value':
            return 'VALUE_ASC';
        case '-value':
            return 'VALUE_DESC';
        default:
        case '-modifiedTime':
            return 'MODIFIED_TIME_DESC';
    }
}

export function getReportFiltersFromUrlParams(urlParams: any): Array<any> {
    return [
        {segment: 'salesDocumentStatuses'},
        {
            period:
                urlParams && urlParams.filter && urlParams.filter.period
                    ? urlParams.filter.period
                    : '-d90',
        },
    ];
}

export function getReportParamsFromUrlParams(urlParams: any): ChartReportParams {
    return {
        gap: urlParams && urlParams.report && urlParams.report.gap ? urlParams.report.gap : 'm',
        dateRangeType: 'period',
        facet: 'count',
        segment: 'salesDocumentStatuses',
    };
}
